import React, { useState, useEffect, useRef } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { sanitizeHtml, stripHtml } from '../utils/sanitize';

interface SlaInstance {
  id: string;
  currentState: string;
  durationSeconds: number;
  elapsedSeconds: number;
  timeRemaining?: number;
  breachedAt?: string;
  achievedAt?: string;
  completedAt?: string;
  discardedAt?: string;
  discardReason?: string;
  isBreached?: boolean;
  currentWarningLevel?: string;
  targetType?: string; // Direct field on instance for compatibility
  SlaPolicy: {
    name: string;
    targetType: string;
    SlaWarningThreshold?: Array<{
      thresholdPercentage: number;
      warningLevel: string;
    }>;
  };
}

interface BreachReasons {
  response?: string;
  ongoing?: string;
  resolution?: string;
}

interface SlaInstancesDisplayProps {
  slaInstances: SlaInstance[] | undefined;
  onBreachDetected?: (instanceId: string) => void;
  breachReasons?: BreachReasons;
  onRequestBreachReason?: (targetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION') => void;
  // Legacy props for backward compatibility
  breachReason?: string;
}

// Helper to check if an SLA instance is in a terminal state (no more monitoring needed)
const isTerminalState = (state: string): boolean => {
  const terminalStates = ['BREACHED', 'ACHIEVED', 'COMPLETED', 'CANCELLED', 'DISCARDED'];
  return terminalStates.includes(state);
};

// Helper to check if all SLA instances are in terminal states
export const areAllSlaInstancesTerminal = (slaInstances: SlaInstance[] | undefined): boolean => {
  if (!slaInstances || slaInstances.length === 0) {
    return true; // No SLAs means nothing to monitor
  }
  return slaInstances.every(instance => isTerminalState(instance.currentState) || instance.isBreached);
};

interface SlaInstanceCardProps {
  instance: SlaInstance;
  onBreachDetected?: (instanceId: string) => void;
  breachReason?: string;
  onRequestBreachReason?: (targetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION') => void;
}

// Warning color scheme - progressively darker as warning level increases
const getWarningColors = (level: string) => {
  switch (level) {
    case 'WARNING_25':
      return {
        bg: 'bg-amber-50 border-amber-200',
        text: 'text-amber-600',
        bar: 'bg-amber-300',
        badge: 'bg-amber-100 text-amber-700 border-amber-300'
      };
    case 'WARNING_50':
      return {
        bg: 'bg-amber-100 border-amber-300',
        text: 'text-amber-700',
        bar: 'bg-amber-400',
        badge: 'bg-amber-200 text-amber-800 border-amber-400'
      };
    case 'WARNING_60':
      return {
        bg: 'bg-orange-50 border-orange-300',
        text: 'text-orange-700',
        bar: 'bg-orange-400',
        badge: 'bg-orange-100 text-orange-700 border-orange-300'
      };
    case 'WARNING_75':
      return {
        bg: 'bg-orange-100 border-orange-400',
        text: 'text-orange-800',
        bar: 'bg-orange-500',
        badge: 'bg-orange-200 text-orange-800 border-orange-500'
      };
    case 'WARNING_90':
      return {
        bg: 'bg-red-50 border-red-400',
        text: 'text-red-700',
        bar: 'bg-red-500',
        badge: 'bg-red-100 text-red-700 border-red-400'
      };
    case 'CRITICAL_95':
      return {
        bg: 'bg-red-100 border-red-500',
        text: 'text-red-800',
        bar: 'bg-red-600',
        badge: 'bg-red-200 text-red-800 border-red-500'
      };
    default: // NONE or default
      return {
        bg: 'bg-white border-blue-300',
        text: 'text-blue-800',
        bar: 'bg-blue-500',
        badge: ''
      };
  }
};

// Get warning level label for display
const getWarningLabel = (level: string) => {
  const labels: Record<string, string> = {
    'WARNING_25': '25%',
    'WARNING_50': '50%',
    'WARNING_60': '60%',
    'WARNING_75': '75%',
    'WARNING_90': '90%',
    'CRITICAL_95': '95%',
    'NONE': ''
  };
  return labels[level] || '';
};

// Calculate warning level from percentage based on thresholds
const calculateWarningLevel = (
  percentage: number,
  thresholds?: Array<{ thresholdPercentage: number; warningLevel: string }>
): string => {
  if (!thresholds || thresholds.length === 0) {
    // Default thresholds if none provided
    if (percentage >= 95) return 'CRITICAL_95';
    if (percentage >= 90) return 'WARNING_90';
    if (percentage >= 75) return 'WARNING_75';
    if (percentage >= 60) return 'WARNING_60';
    if (percentage >= 50) return 'WARNING_50';
    if (percentage >= 25) return 'WARNING_25';
    return 'NONE';
  }

  // Sort thresholds by percentage descending to find the highest triggered level
  const sortedThresholds = [...thresholds].sort(
    (a, b) => b.thresholdPercentage - a.thresholdPercentage
  );

  for (const threshold of sortedThresholds) {
    if (percentage >= threshold.thresholdPercentage) {
      return threshold.warningLevel;
    }
  }

  return 'NONE';
};

// Helper to get target type from instance (prefers direct field, falls back to policy)
const getTargetType = (instance: SlaInstance): string => {
  return instance.targetType || instance.SlaPolicy?.targetType || 'UNKNOWN';
};

// Individual SLA card with real-time updates
const SlaInstanceCard: React.FC<SlaInstanceCardProps> = ({ instance, onBreachDetected, breachReason, onRequestBreachReason }) => {
  const { formatDateTime } = useDateFormatter();

  // Track time offset since last server update for RUNNING instances
  const [localOffset, setLocalOffset] = useState(0);
  // Track if we've locally detected a breach (timer stopped, waiting for server)
  const [localBreachDetected, setLocalBreachDetected] = useState(false);
  const lastUpdateRef = useRef<number>(Date.now());
  const instanceIdRef = useRef<string>(instance.id);
  const breachCallbackFiredRef = useRef<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when server data updates (instance changes)
  useEffect(() => {
    // Reset when instance data changes (new fetch from server)
    if (instanceIdRef.current !== instance.id) {
      setLocalOffset(0);
      setLocalBreachDetected(false);
      breachCallbackFiredRef.current = false;
      lastUpdateRef.current = Date.now();
      instanceIdRef.current = instance.id;
    }

    // If server confirms breach (via state, isBreached flag, or breachedAt timestamp), reset local breach state
    const serverConfirmedBreach = instance.currentState === 'BREACHED' || instance.isBreached || instance.breachedAt;
    if (serverConfirmedBreach && localBreachDetected) {
      setLocalBreachDetected(false);
      breachCallbackFiredRef.current = false;
    }
  }, [instance.id, instance.elapsedSeconds, instance.currentState, instance.isBreached, instance.breachedAt, localBreachDetected]);

  // Real-time timer for RUNNING instances - updates every second
  // DO NOT run timer for terminal states (BREACHED, ACHIEVED, COMPLETED, CANCELLED)
  useEffect(() => {
    // Check if instance is in a terminal state - no monitoring needed
    if (isTerminalState(instance.currentState) || instance.isBreached) {
      // Clear any existing timer and don't start a new one
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    if (instance.currentState !== 'RUNNING') {
      // Clear timer if not running (but not terminal - e.g., PAUSED)
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Reset offset when we get new server data
    setLocalOffset(0);
    setLocalBreachDetected(false);
    breachCallbackFiredRef.current = false;
    lastUpdateRef.current = Date.now();

    timerRef.current = setInterval(() => {
      const secondsSinceUpdate = Math.floor((Date.now() - lastUpdateRef.current) / 1000);
      const baseTimeRemaining = instance.timeRemaining || 0;
      const currentRemaining = baseTimeRemaining - secondsSinceUpdate;

      // Check if breach detected locally
      if (currentRemaining <= 0 && !breachCallbackFiredRef.current) {
        // Stop the timer permanently
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // Mark as locally breached
        setLocalBreachDetected(true);
        breachCallbackFiredRef.current = true;

        // Notify parent ONCE to trigger a single refetch
        if (onBreachDetected) {
          console.log(`⏱️ SLA Breach detected locally for instance ${instance.id} - triggering final server sync`);
          onBreachDetected(instance.id);
        }
        return;
      }

      setLocalOffset(secondsSinceUpdate);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [instance.currentState, instance.elapsedSeconds, instance.timeRemaining, instance.id, instance.isBreached, onBreachDetected]);

  const formatTime = (seconds: number) => {
    const absSeconds = Math.abs(Math.floor(seconds));
    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);
    const secs = absSeconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const isBreached = instance.currentState === 'BREACHED' || instance.isBreached || localBreachDetected;
  const isAchieved = instance.currentState === 'ACHIEVED';
  const isPaused = instance.currentState === 'PAUSED';
  const isCompleted = instance.currentState === 'COMPLETED';
  const isCancelled = instance.currentState === 'CANCELLED';
  const isDiscarded = instance.currentState === 'DISCARDED';
  const isRunning = instance.currentState === 'RUNNING' && !localBreachDetected;
  const showSuccessColors = (isAchieved || isCompleted) && !instance.isBreached;
  // Detect if SLA was breached before conversion (COMPLETED state but isBreached flag is set)
  const wasBreachedBeforeConversion = isCompleted && instance.isBreached;

  // Calculate real-time values for RUNNING instances
  const baseTimeElapsed = instance.elapsedSeconds || 0;
  const baseTimeRemaining = instance.timeRemaining || 0;
  const totalDuration = instance.durationSeconds || 1;

  // Add local offset for running instances (but not if locally breached)
  const timeElapsed = isRunning ? baseTimeElapsed + localOffset :
                      localBreachDetected ? totalDuration : baseTimeElapsed;
  const timeRemaining = isRunning ? baseTimeRemaining - localOffset :
                        localBreachDetected ? 0 : baseTimeRemaining;

  // Calculate percentage
  const percentageElapsed = isBreached || isAchieved || isCompleted
    ? 100
    : isCancelled
    ? Math.min(100, Math.max(0, (baseTimeElapsed / totalDuration) * 100))
    : Math.min(100, Math.max(0, (timeElapsed / totalDuration) * 100));

  // Determine warning level - use server value or calculate from real-time percentage
  const serverWarningLevel = instance.currentWarningLevel || 'NONE';
  const calculatedWarningLevel = calculateWarningLevel(
    percentageElapsed,
    instance.SlaPolicy?.SlaWarningThreshold
  );

  // Use calculated level for real-time display (may be higher than server if time advanced)
  const currentWarningLevel = isRunning
    ? calculatedWarningLevel
    : serverWarningLevel;

  const hasWarning = currentWarningLevel !== 'NONE';
  const warningColors = getWarningColors(currentWarningLevel);

  // Color logic priority:
  // 1. Breached: Red colors
  // 2. Achieved/Completed WITHOUT breach: Green (success)
  // 3. Discarded: Slate/gray colors (condition fields changed)
  // 4. Cancelled: Gray
  // 5. Paused: Yellow
  // 6. Warning levels: Progressive amber -> orange -> red
  // 7. Default/Running: Blue
  const bgColor = isBreached ? 'bg-red-100 border-red-400' :
                 showSuccessColors ? 'bg-green-50 border-green-300' :
                 isDiscarded ? 'bg-slate-100 border-slate-400' :
                 isCancelled ? 'bg-gray-100 border-gray-400' :
                 isPaused ? 'bg-yellow-50 border-yellow-300' :
                 hasWarning ? warningColors.bg :
                 'bg-white border-blue-300';

  const textColor = isBreached ? 'text-red-800' :
                  showSuccessColors ? 'text-green-800' :
                  isDiscarded ? 'text-slate-600' :
                  isCancelled ? 'text-gray-600' :
                  isPaused ? 'text-yellow-800' :
                  hasWarning ? warningColors.text :
                  'text-blue-800';

  const progressBarColor = isBreached ? 'bg-red-600' :
                          showSuccessColors ? 'bg-green-500' :
                          isDiscarded ? 'bg-slate-400' :
                          isCancelled ? 'bg-gray-400' :
                          isPaused ? 'bg-yellow-500' :
                          hasWarning ? warningColors.bar :
                          'bg-blue-500';

  // Check if server has confirmed the breach (even if currentState isn't updated yet)
  const serverConfirmedBreach = instance.currentState === 'BREACHED' || instance.isBreached || !!instance.breachedAt;

  // Display state - show "BREACHED" if server confirmed or locally detected
  const displayState = serverConfirmedBreach ? 'BREACHED' :
                       (localBreachDetected && instance.currentState === 'RUNNING') ? 'BREACHED' :
                       instance.currentState;

  // Only show "(syncing...)" if local breach detected but server hasn't confirmed yet
  const showSyncing = localBreachDetected && !serverConfirmedBreach;

  const warningLabel = getWarningLabel(currentWarningLevel);

  return (
    <div className={`p-4 rounded-lg border-2 ${bgColor} transition-colors duration-500`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className={`text-sm font-semibold ${textColor}`}>
            {getTargetType(instance)} SLA
          </div>
          <div className="text-xs text-gray-600">
            {instance.SlaPolicy.name}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {/* Warning Badge */}
          {hasWarning && !isBreached && !showSuccessColors && !isCancelled && (
            <div className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${warningColors.badge}`}>
              ⚠️ {warningLabel} Warning
            </div>
          )}
          {/* State Badge with Tooltip for breached SLAs */}
          <div className="group relative">
            <div className={`text-xs uppercase font-semibold px-2 py-1 rounded bg-white bg-opacity-50 ${isRunning ? 'animate-pulse' : ''} ${showSyncing ? 'animate-bounce' : ''} ${textColor} ${isBreached ? 'cursor-help' : ''}`}>
              {displayState}
              {showSyncing && (
                <span className="ml-1 text-[10px]">(syncing...)</span>
              )}
            </div>
            {/* Tooltip for breached SLAs */}
            {isBreached && (
              <div className="absolute top-full right-0 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-lg max-w-xs">
                  <div className="font-semibold mb-1">
                    {getTargetType(instance)} SLA: Breached{wasBreachedBeforeConversion ? ' before conversion' : ''}
                  </div>
                  {breachReason && (
                    <div className="text-gray-300 mt-1">
                      <span className="font-medium">Reason:</span>{' '}
                      <span>{stripHtml(breachReason).substring(0, 100) + (breachReason.length > 100 ? '...' : '')}</span>
                    </div>
                  )}
                  {!breachReason && (
                    <div className="text-gray-400 italic">No breach reason provided</div>
                  )}
                </div>
                {/* Tooltip arrow */}
                <div className="absolute bottom-full right-4 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
          <span>Progress</span>
          <span className="font-semibold">{percentageElapsed.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-3 ${progressBarColor} transition-all duration-1000 ease-linear`}
            style={{ width: `${Math.min(100, percentageElapsed)}%` }}
          ></div>
        </div>
      </div>

      {/* Time Information */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-gray-600 mb-1">Time Elapsed</div>
          <div className={`font-semibold ${textColor} ${isRunning ? 'tabular-nums' : ''}`}>
            {formatTime(Math.max(0, timeElapsed))}
          </div>
        </div>
        <div>
          <div className="text-gray-600 mb-1">Time Remaining</div>
          <div className={`font-semibold ${isBreached ? 'text-red-600' : textColor} ${isRunning ? 'tabular-nums' : ''}`}>
            {timeRemaining <= 0 ? '0h 0m 0s' : formatTime(timeRemaining)}
          </div>
        </div>
      </div>

      {/* Breach/Achievement Info */}
      {(instance.breachedAt || localBreachDetected) && (
        <div className="text-xs text-red-600 mt-3 pt-3 border-t border-red-200">
          <span className="font-semibold">Breached:</span>{' '}
          {instance.breachedAt
            ? formatDateTime(instance.breachedAt)
            : 'Just now (confirming...)'}
          {wasBreachedBeforeConversion && (
            <span className="ml-2 text-red-500 italic">(breached before conversion)</span>
          )}
        </div>
      )}
      {instance.achievedAt && (
        <div className="text-xs text-green-600 mt-3 pt-3 border-t border-green-200">
          <span className="font-semibold">Achieved:</span> {formatDateTime(instance.achievedAt)}
        </div>
      )}

      {/* Discarded Info Section - show when SLA was discarded due to condition field changes */}
      {isDiscarded && (
        <div className="mt-3 pt-3 border-t border-slate-300">
          {instance.discardedAt && (
            <div className="text-xs text-slate-600 mb-2">
              <span className="font-semibold">Discarded:</span> {formatDateTime(instance.discardedAt)}
            </div>
          )}
          {instance.discardReason && (
            <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
              <span className="font-semibold">Reason:</span> {instance.discardReason}
            </div>
          )}
        </div>
      )}

      {/* Breach Reason Section - only show when this specific SLA is breached */}
      {isBreached && (
        <div className="mt-3 pt-3 border-t border-red-200">
          {breachReason ? (
            <div>
              <div className="flex items-center text-xs font-semibold text-red-800 mb-1">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                Breach Reason
              </div>
              <div
                className="text-xs text-red-700 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(breachReason) }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-xs text-red-700">No breach reason provided</span>
              {onRequestBreachReason && (
                <button
                  onClick={() => onRequestBreachReason(getTargetType(instance) as 'RESPONSE' | 'ONGOING' | 'RESOLUTION')}
                  className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500"
                >
                  Add Reason
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SlaInstancesDisplay: React.FC<SlaInstancesDisplayProps> = ({
  slaInstances,
  onBreachDetected,
  breachReasons,
  onRequestBreachReason,
  breachReason // Legacy prop for backward compatibility
}) => {
  if (!slaInstances || slaInstances.length === 0) {
    return null;
  }

  // Helper to get breach reason for a specific target type
  const getBreachReasonForTargetType = (targetType: string): string | undefined => {
    if (breachReasons) {
      const normalizedType = targetType.toLowerCase() as 'response' | 'ongoing' | 'resolution';
      return breachReasons[normalizedType];
    }
    // Fall back to legacy breachReason for backward compatibility
    return breachReason;
  };

  return (
    <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {slaInstances.map((instance) => (
          <SlaInstanceCard
            key={instance.id}
            instance={instance}
            onBreachDetected={onBreachDetected}
            breachReason={getBreachReasonForTargetType(getTargetType(instance))}
            onRequestBreachReason={onRequestBreachReason}
          />
        ))}
      </div>
    </div>
  );
};

export default SlaInstancesDisplay;
